/**
 * Confirmation dialog for merging one personal bundle into another. The person picks the bundle to
 * keep; the dialog asks the server for a dry-run preview (how many activities move and what purpose
 * the kept bundle ends up with) and only then enables the merge. The merge itself, its purpose rule
 * and the Canvas redirect run in Rust (`POST /api/workspace/bundles/:id/merge`).
 *
 * @module apps/web/src/components/workspace/MergeBundleDialog
 */

import { useEffect, useMemo, useState } from 'react';
import type { WorkspaceBundleUI } from '@fluxability/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api, type BundleMergePreview } from '@/lib/api';
import { useWorkspaceStore } from '@/stores/workspaceStore';

/** Inputs of the merge dialog. */
export interface MergeBundleDialogProps {
  /** Bundle that will be merged away and archived; null keeps the dialog closed. */
  source: WorkspaceBundleUI | null;
  /** Called when the dialog closes, after a merge or a cancel. */
  onClose: () => void;
  /** Called with the moved activity count after a successful merge. */
  onMerged?: (movedActivityCount: number, target: WorkspaceBundleUI) => void;
}

/** Plural-aware activity count, for example "1 activity" or "3 activities". */
function activities(count: number): string {
  return `${count} ${count === 1 ? 'activity' : 'activities'}`;
}

/**
 * Pick the bundle to merge `source` into, show the server's preview, and merge on confirmation.
 * Errors from the preview or the merge are shown in the dialog and leave it open.
 */
export function MergeBundleDialog({ source, onClose, onMerged }: MergeBundleDialogProps) {
  const bundles = useWorkspaceStore(s => s.bundles);
  const mergeBundle = useWorkspaceStore(s => s.mergeBundle);
  const [targetId, setTargetId] = useState('');
  const [preview, setPreview] = useState<BundleMergePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);
  const sourceId = source?.id;

  const targets = useMemo(
    () => bundles.filter(bundle => bundle.status === 'active' && bundle.id !== sourceId),
    [bundles, sourceId]
  );
  const target = targets.find(bundle => bundle.id === targetId) || null;

  useEffect(() => {
    setTargetId('');
    setPreview(null);
    setError(null);
  }, [sourceId]);

  useEffect(() => {
    if (!sourceId || !targetId) return;
    let cancelled = false;
    setPreview(null);
    setError(null);
    api.workspace
      .previewBundleMerge(sourceId, targetId)
      .then(response => {
        if (cancelled) return;
        if (response.success && response.data) setPreview(response.data.preview);
        else setError(response.error?.message || 'The merge could not be previewed.');
      })
      .catch(reason => {
        if (!cancelled)
          setError(reason instanceof Error ? reason.message : 'The merge could not be previewed.');
      });
    return () => {
      cancelled = true;
    };
  }, [sourceId, targetId]);

  async function confirm() {
    if (!source || !target) return;
    setMerging(true);
    const result = await mergeBundle(source.id, target.id);
    setMerging(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onMerged?.(result.value.moved_activity_count, target);
    onClose();
  }

  return (
    <Dialog open={Boolean(source)} onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge {source?.name || 'bundle'} into…</DialogTitle>
          <DialogDescription>
            Every activity in {source?.name || 'this bundle'} moves to the bundle you choose. That
            bundle keeps its name and colour, and {source?.name || 'this bundle'} is archived.
            Future Canvas imports for {source?.name || 'it'} land in the bundle you choose.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>Keep this bundle</Label>
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a bundle" />
            </SelectTrigger>
            <SelectContent>
              {targets.map(bundle => (
                <SelectItem key={bundle.id} value={bundle.id}>
                  {bundle.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {targets.length === 0 && (
            <p className="text-muted-foreground text-sm">Create another bundle to merge into.</p>
          )}
        </div>
        {preview && target && (
          <div className="bg-muted/30 rounded-md border p-3 text-sm">
            <p className="font-medium">
              {activities(preview.activity_count)} will move into {target.name}
              {preview.open_activity_count !== preview.activity_count &&
                ` (${preview.open_activity_count} still open)`}
              .
            </p>
            <p className="text-muted-foreground mt-1">
              {preview.target_purpose
                ? `Purpose after the merge: ${preview.target_purpose}`
                : 'The merged bundle will still need a purpose.'}
            </p>
          </div>
        )}
        {error && <p className="text-destructive text-sm">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={merging}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={!preview || merging}>
            {preview ? `Merge ${activities(preview.activity_count)}` : 'Merge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
